import {action, computed, observable, _, uiConstants} from '@api'
import {FilterDefinition, vlpFilterDefinition} from '@api/graphql'
import { SyncTriggers } from '@design-board'
import {VLP_PLAN_GROUP, VLP_COLLECTION_GROUP, VLP_FOLDER_GROUP, deployments, CatalogStore} from '@stores'
import {IMerchCollectionVersionC8} from '@stores/c8/C8ServicesStore'
import {CatalogInstanceChildStore} from '@stores/catalog/CatalogInstanceChildStore'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {removeUndefinedsNested} from '@utils'
import {autorun, reaction} from 'mobx'
import {computedFn, IDisposer} from 'mobx-utils'
import { DEBUG_VLP } from '.'

const ROOT_ID = '<r>'
export type VLPHierarchyTypeName = 'folder' | 'plan' | 'collection'
/**
 * Called 'folders' elsewhere, this is the raw field in the VLP hierarchy:
 * Plan -> Folder(s) -> Collections -> CollectionVersions
 */
export const VLP_SUBCATEGORIES = 'subCategories'
/**
 * Used to calculate the length of folders (subCategories) on mpvs at runtime from ES
 */
export const VLP_FOLDER_LENGTH_RUNTIME_MAPPING_ID = 'subCategories_length'
export const VLP_FOLDER_LENGTH_RUNTIME_MAPPING = {
  [VLP_FOLDER_LENGTH_RUNTIME_MAPPING_ID]: {type: 'long', script: "emit(doc['subCategories.id'].length);"}
}

export interface IVlpHierarchyCatalogItem {
  plan?: IPlanDefinition
  subCategories?: Array<IPlanSubCategory>
  collection?: IPlanCollection
  collectionVersion?: IMerchCollectionVersionC8
}
export interface IPlanCollection {
  name: string,
  exId: string,
  id: string,
  // This is unreliable - I have found it not filled in for numerous collections on vip test
  subCategories: Array<Partial<IPlanSubCategory>>,
  plan: Partial<IPlanDefinition>
  versions: Array<IVersion>

  folder?: { id: string, exId: string, name: string, path: string, level: number }

  /**
   * Added after the fact by graphql service
   */
  lastSyncTime?: string
}

export interface IVersion {
  exId: string,
  id: string,
  name: string
}

export interface IPlanSubCategory {
  id?: string,
  exId?: string,
  name?: string
  leaf?: boolean,
  level?: number,
  parent?: string & { id: string, name?: string } // todo remove when deepak updates server index
  collections?: Array<IPlanCollection>
  path?: string
  plan?: { exId: string, name: string, _id: string }
}

export interface IPlanDefinition {
  id?: string,
  exId?: string,
  name?: string,
  versions?: Array<any>,
  subCategories?: Array<IPlanSubCategory>,

  active?: boolean
  leaf?: boolean
}

export interface IVLPHierarchySelection {
  plans?: Array<string>,
  /**
   * At each level of the hierarchy one or more folders can be selected
   * Each unique path should be stored in this array
   *
   * Examples:
   * Carters -> Baby -> Dress, One Piece
   * Carters -> Toddler -> Hats
   */
  folders?: Array<Array<string>>,
  collections?: Array<string>
}

export class VLPHierarchyStore extends CatalogInstanceChildStore {
  constructor(public catalog: CatalogStore) {
    super(catalog)
    synchronizeStores(() => {
      // observe(this.selected, 'plans', ({oldValue}) => {
      //   console.trace(toJS(this.selected))
      // })

      const {catalog, catalog: {stores, stores: {authStore, workspaces}}} = this

      this._toDispose.push(
        autorun(async () => {
          const {userIsAuthenticated} = authStore, {deploymentConfig} = workspaces
          // if (userIsAuthenticated && workspaces?.deploymentConfig && workspaceConfig?.seasonSelectors?.some(s => s.type == 'merch')) {

          // If any of our workspaces don't use C8
          if (userIsAuthenticated && workspaces?.deploymentConfig) {
            const seasonalWorkspaces = deploymentConfig?.workspaces?.filter(ws => !_.isEmpty(ws.seasonSelectors)) ?? []

            if (!_.isEmpty(seasonalWorkspaces)) {
              // DEBUG && trace()
              if (seasonalWorkspaces.some(ws => ws.breadcrumbSource === 'c8')) {
                const {c8: {services: {c8ForCurrentWorkspace}}} = stores
                if(c8ForCurrentWorkspace?.c8PlanHierarchy) {
                  this.setPlanHierarchy(c8ForCurrentWorkspace.c8PlanHierarchy)
                }
                else {
                  DEBUG_VLP && console.debug(`Waiting for c8Plan to finish loading vlp hierarchy`)
                }
              } else {
                if (seasonalWorkspaces.some(ws => ws.breadcrumbSource === 'es' || !ws.breadcrumbSource)) {
                  const plans = await this.loadPlansES()
                  plans && this.setPlanHierarchy(plans)
                }
              }
            }
          }
        }, {name: `Load Plan Hierarchy for VLPHierarchyStore`}),

        reaction(() => ({
          progressAndSelectedCollections: this.selectedCollections.filter(c => c && !stores.syncProgress.byId.get(c.exId)?.itemId)
        }), async ({progressAndSelectedCollections}) => {
          if (_.size(progressAndSelectedCollections)) {
            await Promise.all(progressAndSelectedCollections.map(async c => await this.getHierarchySelectionLastSync(c)))
            this.selectedSyncCollections = this.selectedCollections
          }
        }, {name: 'update last sync time of collection'}))
    })
  }

  _toDispose: Array<IDisposer> = []

  @action dispose = () => this._toDispose.forEach(d => d())

  @observable.ref plans: {
    plans?: Array<IPlanDefinition>,
    loading?,
    error?
  } = {}
  @observable selectedSyncCollections: Array<IPlanCollection>

  @computed get inactivePlans() {
    return (this.plans?.plans ?? []).filter(p => p.active === false)
  }

  @computed get folders() {
    return _.flatMap(this.plans?.plans ?? [], p => p?.subCategories.map(sc => {
      const plan = {...p, type: 'plan'}
      return ({...sc, plan, type: 'folder', parent: sc.parent ?? plan})
    }))
  }

  @computed get hasHierarchyItemsSelected() { return _.values(this.selected)?.some(item => !_.isEmpty(item)) }

  vlpDataFieldNames = ['id', 'exId', 'name']

  parseHierarchyGroup = (name: string): { type: VLPHierarchyTypeName, folderDepth?: number } => {
    const {catalog} = this,
      esField = catalog.esFieldFor_byField(name)

    const type = name === VLP_PLAN_GROUP ? 'plan' :
                 name === VLP_COLLECTION_GROUP || (esField === 'collection' && catalog._fieldByName['plan']) // Extra check as RH has a 'collection' field but isn't actually VLP
                 ? 'collection'
                 : name.startsWith(VLP_FOLDER_GROUP) ? 'folder' : null,
      folderDepth = type === 'folder' ? parseInt(name.substring(VLP_FOLDER_GROUP.length + 1)) : null

    return type ? {type, folderDepth} : null
  }

  @computed get distinctHierarchyPathNames() {
    const {plans, esPlans} = this,
      p = plans ?? esPlans
    return p
  }

  availableCollectionsFor = computedFn<(leafSelections, selected) => Array<IPlanCollection>>((leafSelections, selected) => {
    const {planById, collectionsForFolder, collections, collectionById} = this

    // console.log({leafSelections, selected})
    return _.isEmpty(leafSelections) ? collections : _.uniq(_.flatMap(leafSelections.map(({type, id}) => type == 'collection' ? collectionById[id] : type == 'folder' ? collectionsForFolder(id, true) : type === 'plan' ? _.flatMap(planById[id]?.subCategories.map(sc => sc.collections)) : []))).filter(_.identity) as IPlanCollection[]
  })

  @computed get allAvailableCollections() {
    const {availableCollectionsFor, leafSelections, selected} = this
    return availableCollectionsFor(leafSelections, selected)
  }

  @computed get collections(): Array<IPlanCollection & any> {
    return _.flatMap(this.folders, f => (f.collections ?? []).map(c => ({...c, folder: f, type: 'collection', subCategories: _.isEmpty(c.subCategories) ? [f] : c.subCategories})))
  }

  @computed get hasSelections() {
    const {selected: {collections, folders, plans}} = this
    return !_.isEmpty(collections) || !_.isEmpty(folders) || !_.isEmpty(plans)
  }

  @computed get planById() {
    return _.keyBy(this?.plans?.plans ?? [], v => v.id)
  }

  leafSelectionsFor: (selected: IVLPHierarchySelection) => Array<{ type: 'folder' | 'plan' | 'collection' | string, id: string }> = computedFn((selected: IVLPHierarchySelection) => {
    // trace()

    if (!this.hasLoadedHierarchy) return []

    const {planById, collectionsForFolder} = this

    // Remove all folder hierarchies that are direct parents of our selected collections
    let folderHierarchies = (selected.folders ?? []).filter(h => {
      const leafFolderId = _.last(h)
      return !collectionsForFolder(leafFolderId, true).some(c => _.includes(selected.collections, c.id))
    })

    return [
      ...(selected.collections ?? []).map(id => ({type: 'collection', id})),
      ...folderHierarchies.map(h => ({type: 'folder', id: _.last(h)})),
      ...(selected.plans ?? []).filter(p => !planById[p]?.subCategories?.some(sc => sc.level == 1 && selected.folders.some(h => h && h[0] == sc.id))).map(p => ({type: 'plan', id: p}))
    ]
  })

  @computed get leafSelections() {
    // trace()
    return this.leafSelectionsFor(this.selected)
  }

  hierarchyTermsFor = computedFn((ignore?: Partial<vlpFilterDefinition & FilterDefinition>) => {
    const {leafSelections} = this
    return leafSelections.filter(s => (s.type === 'folder' && !ignore.vlpFolder)
                                      || (s.type === 'plan' && !ignore.vlpPlan)
                                      || (s.type === 'collection' && !ignore.vlpCollection))
  })

  @observable.ref esPlans: Array<IPlanDefinition>

  @computed.struct get childrenOfId() {
    const {esPlans} = this
    if (!esPlans) return {}

    return _.groupBy(_.flatMap(esPlans.map(p => p.subCategories)), (v: IPlanSubCategory) => v?.parent?.id ?? v?.parent ?? ROOT_ID)
  }

  @computed get deepestFolderIndex() {
    return _.max(_.keys(this.foldersByDepth).map(v => parseInt(v)))
  }

  @computed.struct get foldersForCollection(): { [collectionId: string]: Array<IPlanSubCategory> } {
    const {folderById, pathToFolder, collections} = this

    return collections.reduce((acc, c) => {
      return _.assign(acc, {[c.id]: pathToFolder(c.folder.id).map(id => folderById[id])})
    }, {})
  }

  @computed.struct get folderById(): { [id: string]: IPlanSubCategory } {
    const {esPlans} = this
    if (!esPlans) return {}

    return _.keyBy(_.flatMap(esPlans.map(p => p.subCategories)), c => c?.id ?? c?.exId)
  }

  collectionsForFolder = computedFn((folderId: string, traverseChildrenFolders: boolean) => {
    const {folderById, childrenOfId, collectionsForFolder} = this
    const folder = folderById[folderId]
    if (!folder) return []

    return _.flatMap([...(folder.collections ?? []).map(c => ({...c, folder})), ...((!traverseChildrenFolders ? [] : (childrenOfId[folderId] ?? [])).map(({id}) => collectionsForFolder(id, traverseChildrenFolders)))])
  })

  @computed get foldersByDepth(): { [depth: number]: Array<IPlanSubCategory> } {
    const {folderById} = this
    return _.groupBy(_.values(folderById), f => f.level - 1)
  }

  @action loadPlansES = async () => {
    const {workspaceConfig} = this.workspaces,
      {es} = this
    if (workspaceConfig) {
      DEBUG && console.trace(`Loading plans via ES aggregation - ${deployments.selectedDeploymentId}`)
      const {hits: {hits}} = await es.search({
        meta: 'vlp.loadPlansES()',
        body: {
          'sort': [{'name': {'order': 'asc'}}]
        },
        index: workspaceConfig.seasonIndex
      }, {log: {query: false, result: false}})
      DEBUG && console.debug(`${hits.length} plans loaded from ES`)
      return hits.map(({_id, _source}) => ({_id, ..._source}))
    }
  }

  @computed get hasVlpHierarchy() {
    return this.catalog.seasonSelectorFields.some(f => f.field === 'plan')
  }

  @observable hasLoadedHierarchy = false
  @action setPlanHierarchy = (_plans) => {
    // console.trace(`setPlanHierarchy`, _plans)
    const {catalog} = this
    this.esPlans = _.isEmpty(catalog.workspaceConfig?.seasonIncludedPlans) ? _plans : _plans.filter(({name}) => _.includes(catalog.workspaceConfig.seasonIncludedPlans, name))
    const {esPlans} = this
    this.plans = {plans: esPlans}
    //if (!_.isEmpty(_plans)) {
    this.hasLoadedHierarchy = true
    // }

    const {selected} = this

    this.selected = {
      plans: selected.plans || [],
      collections: selected.collections || [],
      folders: selected.folders || []
    }

    if (!_.isEmpty(selected.collections)) {
      const {groupBy: {hierarchy, removeHierarchyLevel}} = catalog
      hierarchy.length > 0 && hierarchy[0] === 'merchCollections' && removeHierarchyLevel(0)
      hierarchy.length > 1 && hierarchy[1] === 'merchCollections' && removeHierarchyLevel(1)
    }

    // Verify the plan/collection/folder is still valid
    const invalidPlans = !_.isEmpty(selected.plans) ? selected.plans?.filter(planId => !esPlans.some(p => p.id === planId || p.exId === planId)) : []
    if (!_.isEmpty(invalidPlans)) {
      console.debug(`Some plans in the selection set were not found.  The vlp hierarchy has been reset.`, invalidPlans)
      Object.assign(selected, {plans: undefined, folders: [], collection: undefined})
    }
    return this.plans
  }

  /**
   * Folder levels in the Hierarchy.
   */
  @observable _merchFolderLevel = 0

  /**
   * @computed: get folder levels of the Hierarchy
   */
  @computed get merchFolderLevel() {
    return this._merchFolderLevel
  }

  @action updateMerchFolderLevel = (level) => {
    this._merchFolderLevel = level > 0 ? level : 4 //FIXME - fix as its not working on server
  }

  /**
   * Selected values from the Hierarchy.
   */
  @computed.struct get selected(): IVLPHierarchySelection {
    let r = this.catalog.settings.vlp?.selected ?? {}

    return removeUndefinedsNested({...r, folders: r.folders ? r.folders.filter(_.identity) : undefined})
  }

  set selected(v) {
    _.merge(this.catalog.settings, {vlp: {selected: v}})
  }

  isSelected = (type: string, id: string, hierarchy?: IVLPHierarchySelection) => {
    const selected = hierarchy ?? this.selected

    switch (type) {
      case 'plan':
      case 'plans':
        return _.includes(selected.plans, id)

      case 'folder':
      case 'folders':
        const {folderById} = this, {folders} = selected
        const selectedFolders = hierarchy ? _.uniq(_.flatMap((folders ?? []).map(h => (h ?? []).map(id => folderById[id]))).filter(_.identity)) : this.selectedFolders

        return selectedFolders.some(f => f.exId == id)

      case 'collection':
      case 'collections':
        return _.includes(selected.collections, id)

      default:
        return false
    }
  }

  @computed get selectedFoldersById() {
    return _.keyBy(this.selectedFolders, f => f.exId ?? f.id)
  }

  findSelectedChildren = (type: string, id: string) : Array<any> => {
    const {selected, selectedCollections, folderById, selectedFoldersById} = this

    switch (type) {
      case 'plan':
      case 'plans':
        const selectedFoldersInPlan = selected.folders.filter(h => folderById[_.first(h)]?.plan?.exId === id).map(h => h.map(id => folderById[id])),
          selectedCollectionsInPlan = selectedCollections.filter(c => c?.plan?.exId === id)

        return [...selectedCollectionsInPlan, ...selectedFoldersInPlan]

      case 'folder':
      case 'folders':
        return selectedCollections.filter(c => c.folder?.id === id)

      case 'collection':
      case 'collections':
        return []

      default:
        return []
    }
  }

  @computed get folderDepth() {
    return _.size(this.folderByDepthForSelection)
  }

  @computed get folderByDepthForSelectionDict() {
    const {folderByDepthForSelection} = this

    return _.keys(folderByDepthForSelection).reduce((acc, k) => _.assign(acc, {[k]: _.keyBy(folderByDepthForSelection[k], f => f.id)}), {})
  }

  /**
   * Given the selected plan(s), what folders can be selected?
   */
  @computed get folderByDepthForSelection(): { [level: number]: Array<IPlanSubCategory> } {
    const {selectedPlans} = this

    if (_.isEmpty(selectedPlans)) return this.foldersByDepth

    return _.groupBy(_.flatMap(selectedPlans.map(p => p.subCategories)), v => v.level - 1)
  }

  @computed get availablePlans() {
    const {catalog: {workspaceConfig}} = this,
      {plans: {plans}} = this,
      seasonIncludedPlans = workspaceConfig?.seasonIncludedPlans

    return !plans ? null : plans.filter(p => p.active !== false && (!seasonIncludedPlans?.length || seasonIncludedPlans?.includes(p.name)))
  }

  @computed get selectedPlansById() {
    return _.keyBy(this.selectedPlans, p => p.id)
  }

  @computed get selectedPlans(): Array<IPlanDefinition> {
    const {esPlans, selected: {plans}, planById} = this
    return esPlans && !_.isEmpty(plans) ? plans.map(id => planById[id]).filter(_.identity) : []
  }

  @computed get planByName() {
    return _.keyBy(this.plans.plans, p => p.name)
  }

  @computed get folderByName() {
    return _.groupBy(this.folders, p => p.name)
  }

  @computed get collectionByName() {
    return _.groupBy(this.collections, p => p.name)
  }

  @computed get selectedFoldersAsText() {
    const {selected: {folders}, folderById} = this

    if (_.isEmpty(folders)) return ''

    const maxDepth = _.max(folders.map(f => f.length))
    return _.range(maxDepth).map(i => folders.map(h => h[i] ? folderById[h[i]].name : null).filter(_.identity).join(', ')).join(' → ')
  }

  @computed get selectedFolders(): Array<IPlanSubCategory> {
    const {selected: {folders}, folderById} = this
    return _.uniq(_.flatMap((folders ?? []).map(h => (h ?? []).map(id => folderById[id]))).filter(_.identity))
  }

  @computed get selectedFoldersAtDepth() {
    const {selectedFolders} = this
    return _.keyBy(selectedFolders, f => f.level)
  }

  @observable collectionSeasons: {
    id: string
    name: string
    versions: Array<{ id, name }>
  }

  @action getHierarchySelectionLastSync = async (syncObj) => {
    const {es} = this
    if (syncObj?.exId) {
      const data = await es.search({
        meta: 'vlp.collectionLastSync',
        body: {
          query: {
            match: {
              id: syncObj.exId
            }
          },
          sort: [
            {
              timestamp: {
                order: 'desc'
              }
            }
          ],
          _source: 'payload',
          size: 1
        },
        index: es.indexes.changeHistory
      })

      if (data?.hits?.hits) {
        const {hits: {hits}} = data
        const r = JSON.parse(_.first(hits)?._source?.payload || '{}').data?.after?.syncTime
        syncObj.lastSyncTime = r
        return r
      }

      return null
    }
  }

  @computed get collectionVersionFilter() {
    return this.catalog.filter.state.groups.collectionVersion
  }

  @computed get selectedDevCollectionIds() {
    const {catalog: {filter: {selectedCollectionIds}}} = this
    return selectedCollectionIds
  }

  @computed get collectionById() : Record<string, IPlanCollection> {
    const {collections} = this
    return _.keyBy(collections, c => c.id ?? c.exId)
  }

  @computed get selectedCollections() {
    const {collectionById, selected: {collections}} = this
    return (collections ?? []).map(id => collectionById[id]).filter(_.identity)
  }

  pathTitleForFolder = computedFn((folderId: string) => {
    const {pathToFolder, folderById} = this
    return pathToFolder(folderId).map(id => {
      const f = folderById[id]
      return f?.name
    }).join(' → ')
  })

  @observable lastSyncTimeMap: Map<string, any> = new Map()

  @observable collectionVersionCountMap: Map<string, any> = new Map()

  fetchLastSyncTime = async (trigger:SyncTriggers|'internal', syncId?:string) => {
    if(trigger !== 'filter'){
      const {catalog: {vlp: {hierarchy: {lastSyncTimeMap, selectedCollections, getHierarchySelectionLastSync}}}, selectedDevCollectionIds} = this
      const ids = []
      lastSyncTimeMap?.clear()
      selectedCollections.forEach((collection: IPlanCollection) => {
        collection.versions.forEach((version) => {
          const id = collection.id + uiConstants.SEPARATOR + version.id
          ids.push(id)
        })
      })

      selectedDevCollectionIds?.forEach(collId => {
        ids.push(collId)
      })

      await Promise.all(ids.map(async id => {
        const lastSyncTime = await getHierarchySelectionLastSync({exId: id})
        lastSyncTimeMap.set(id, lastSyncTime)
      }))
    }
  }

  fetchCollectionVersionCount = async () => {
    const {es, vlp: {hierarchy: {filteredCollectionVersionAggregations, selectedCollections, collectionVersionCountMap}}} = this
    // console.log({...filteredCollectionVersionAggregations})
    if (filteredCollectionVersionAggregations) {
      const {aggregations} = await es.search({...filteredCollectionVersionAggregations})
      // console.log(aggregations)
      _.keys(aggregations).forEach((key) => { // key is collectionID
        const buckets: Array<any> = aggregations[key]['version_item_count']['buckets']
        buckets?.forEach((countObj) => {
          const id = key + uiConstants.SEPARATOR + countObj['key']
          collectionVersionCountMap.set(id, countObj['doc_count'])
        })
      })
    }
  }

  @computed get filteredCollectionVersionAggregations() {
    const {workspaces, es, vlp: {hierarchy: {selectedCollections}}} = this,
      collectionAgg = {},
      {workspaceConfig} = workspaces
    selectedCollections?.forEach((collection) => {
      collectionAgg[collection.id] = {
        filter: {
          term: {
            'collection.id': collection.id
          }
        },
        aggs: {
          version_item_count: {
            terms: {
              field: 'collectionVersion.id'
            }
          }
        }
      }
    })

    const queryObj = {
      meta: 'VLPHierarchyStore:filteredCollectionVersionAggregations',
      body: {
        aggs: collectionAgg,
        //query: es.filterForNonDeletedItems,
        size: 0
      },
      index: workspaceConfig?.catalog?.index
    }
    return queryObj
  }

  @computed get selectedCollectionVersions() {
    return this.selectedCollections?.reduce((acc, collection) => {
      return _.unionBy(acc, (collection?.versions ?? []), 'exId')
    }, [])
  }

  pathToFolder = computedFn((folderId: string) => {
    const {folderById} = this,
      r = []

    r.unshift(folderId)
    let parent = folderById[folderId]?.parent?.id
    while (parent) {
      r.unshift(parent)
      parent = folderById[parent]?.parent?.id
    }
    return r
  })

  collectionContainedBy = computedFn((collectionId: string, otherId: string) => {
    const {collectionById, planById, folderById} = this,
      c = collectionById[collectionId]

    if (!c) return false

    if (planById[otherId]) {
      return c.plan.id === planById[otherId].id
    }

    if (folderById[otherId]) {
      let currentFolder = folderById[c.folder.id]
      while (currentFolder) {
        if (currentFolder.id === otherId) return true
        if (!currentFolder.parent) return false

        currentFolder = folderById[currentFolder.parent.id]
      }
    }

    return false
  })

  @computed get leafFolderLevel() {
    const {folderById, selectedFolders} = this
    return parseInt(_.max(_.keys(_.groupBy(selectedFolders, f => f.level))))
  }

  // @action changeSelected_debounced = _.debounce((type: string, value: string, folderIndex?: number, hierarchyToUpdate?: IVLPHierarchySelection): boolean => this.changeSelected(type, value, folderIndex, hierarchyToUpdate), 200)

  /**
   * @action: remove selected hierarchy
   */
  @action changeSelected = (type: string, value: string, folderIndex?: number, hierarchyToUpdate?: IVLPHierarchySelection): boolean => {
    const selected = hierarchyToUpdate ?? this.catalog.settings.vlp?.selected

    //console.trace(`changeSelected - before`, toJS({type, value, folderIndex, selected: toJS(selected), hierarchyToUpdate}, {recurseEverything: true}))

    const plans = selected.plans ?? [],
      folders = (selected.folders ?? []),
      collections = (selected.collections ?? []),
      {
        catalog: {groupBy: {hierarchy}},
        folderById, collectionById, planById
      } = this

    let unselect: boolean,
      newFolders: Array<Array<string>>

    switch (type) {
      case 'plan':
      case 'plans':
      case 'MerchPlans':
        unselect = _.includes(plans, value)
        newFolders = unselect ? folders.filter(h => folderById[h[0]]?.plan?._id != value) : folders
        const newPlans = unselect ? plans.filter(p => p != value) : [...plans, value]
        Object.assign(selected, {
          plans: newPlans,
          folders: newFolders,
          collections: _.isEmpty(newPlans) ? [] : unselect ? collections.filter(c => collectionById[c].plan.id !== value) : collections
        })
        break
      case 'folder':
      case 'folders':
        // Is this folder already selected (deselect case)
        const hierarchyItem = folderById[value]
        unselect = this.isSelected('folders', value, hierarchyToUpdate)  // Which unique hierarchy is this folder in?

        if (unselect) {
          // Folder in hierarchy is being removed, remove this folder and any children folders in its hierarchy whereever it is found
          newFolders = folders.map(h => {
            if (h[folderIndex] === value) {
              const r = _.take(h, folderIndex)

              // If we have two folders underneath the same parent and we remove one of the leaves, we want to remove the entire path since it'll already be included by the sibling
              if (_.isEmpty(r) || folders.some(other => h != other && _.includes(other, _.last(r)))) {
                return null
              }

              return r
            }

            return h
          }).filter(_.identity)
        } else {
          // Either add this folder as a child to an existing path that ends at its parent or create a new hierarchy with the full path to the node

          const indexWithParentAsLeaf = folders.findIndex(path => _.last(path) === hierarchyItem?.parent?.id)

          if (indexWithParentAsLeaf === -1) {
            // console.log('index has leaf')
            newFolders = [...folders, this.pathToFolder(value)]
          } else {
            // console.log('no leaf')
            newFolders = _.cloneDeep(folders)
            newFolders[indexWithParentAsLeaf].push(value)
          }
          newFolders.filter(v => v && !_.isEmpty(v.filter(_.identity)))
        }
        // console.log({newFolders})
        Object.assign(selected, {
          plans,
          folders: newFolders, //folders?.includes(value) ? _.take(folders, folderIndex) : [..._.take(folders , folderIndex), value],
          collections: unselect ? collections.filter(c => !this.collectionContainedBy(c, value)) : collections
        })

        break
      case 'collection':
      case 'collections':
        unselect = collections.find(v => v === value) != null
        Object.assign(selected, {
          plans,
          folders,
          collections: unselect ? collections.filter(v => v !== value) : [...(this.selected.collections ?? []), value]
        })

        const {catalog, syncProgress} = this

        if (!hierarchyToUpdate) {
          value && syncProgress.isMerchCollectionSyncing(value, this.versionsToSync)
        }

        if (hierarchy[0] === 'merchCollections') {
          catalog.groupBy.hierarchy = []
          hierarchy.splice(1, 1)
          //!hierarchyToUpdate && updateQueryParams({groupBy: ''})
        }
        if (hierarchy[1] === 'merchCollections') {
          hierarchy.splice(1, 1)
          //!hierarchyToUpdate && updateQueryParams({groupBy: groupBy.split(',').filter((group) => group != 'merchCollections').join(',')})
        }

        break

      default:
        console.error(`Unhandled hierarchy nav:`, {type, value, folderIndex})
        break
    }

    // console.log(`changeSelected - after`, {type, value, folderIndex, after: toJS(selected)})
    return unselect
  }

  @computed get versionsToSync() {
    return this.catalog.settings.sync?.seasons
  }

  getCollections = (category) => {
    if (category) {
      if (category.subCategories && category.subCategories.length) {
        return _.flatMap(_.map(category.subCategories, thisSubCategory => this.getCollections(thisSubCategory)))
      } else if (category.children && category.children.length) {
        return _.flatMap(_.map(category.children, thisChild => this.getCollections(thisChild)))
      } else if (category.collections) {
        return category.collections
      }
    }
    return []
  }

  @action clear = () => {
    this.catalog.settings.vlp.selected = {plans: undefined, collections: [], folders: []}
  }

  @action clearFolderLevel = (index: number) => {
    const {catalog: {settings: {vlp: {selected}}}} = this
    selected.folders = _.uniq(selected.folders.map(h => _.take(h, index)).filter(h => !_.isEmpty(h)))
    selected.collections = []
  }

  itemOfType = computedFn((type: 'folder' | 'collection' | 'plan' | string, v: string, otherValues: { [key: string]: any }): IPlanCollection | IPlanSubCategory | IPlanDefinition => {
    const dict = this[`${type}ById`]
    let r = !v || !dict ? null : dict[v]

    if (!r && dict) {
      // Try by name
      let filtered = _.values(dict).filter(item => item.name === v),
        hierarchyFields = otherValues ? _.keys(otherValues).filter(k => this.parseHierarchyGroup(k)) : []

      if (!_.isEmpty(hierarchyFields)) {
        filtered = filtered.filter(item => hierarchyFields.every(hf => {
          const {type: hgType, folderDepth} = this.parseHierarchyGroup(hf),
            hfValue = otherValues[hf]

          if (v === hfValue && type === hgType) return true
          else if (hgType === 'folder') {
            return item?.folder?.id === hfValue && item?.folder?.level === folderDepth + 1
          } else if (hgType === 'plan') {
            return item?.plan?.id === hfValue
          }

          return false
        }))
      }

      if (filtered.length > 1) {
        // debugger
      }

      return _.first(filtered)
    }

    return r
  })

  itemsFor = computedFn((type: 'folder' | 'collection' | 'plan', v: string, otherValues: { [key: string]: any }) => {
    const {itemOfType} = this,
      item = itemOfType(type, v, otherValues),
      collection = type === 'collection' ? item as IPlanCollection : null,
      folder = type === 'folder' ? item as IPlanSubCategory : null,
      plan = collection?.plan ?? folder?.plan ?? (type === 'plan' ? item as IPlanDefinition : null)

    return item ? {collection, folder, plan} : null
  })

  folderPathToRoot = (folder: IPlanSubCategory, pickFields = this.vlpDataFieldNames) => {
    if (!folder) return null

    const {folderById} = this
    let r = [], current = folder

    while (current) {
      r = [_.pick(current, pickFields), ...r]
      current = current.parent ? folderById[current.parent.id] : null
    }

    return r
  }

  isMagicHierarchyField = (field: string) => {
    return field.startsWith('VLP_')
  }

  @computed get availableCollectionVersionById() : Record<string, IVersion> {
    const {allAvailableCollections} = this
    return _.keyBy(allAvailableCollections.map(v => v.versions).flat(), cv => cv.id)
  }

  @computed get offlineItemsByVlpHierarchyId() {
    const {catalog: {filteredOfflineItems}} = this
    return filteredOfflineItems.reduce((acc, item : IVlpHierarchyCatalogItem) => {
      const {plan, subCategories, collection, collectionVersion} = item

      if (plan?.id) {
        if (!acc[plan.id]) {
          acc[plan.id] = []
        }

        acc[plan.id].push(item)
      }

      if (subCategories) {
        subCategories.forEach(({id}) => {
          if (!acc[id]) {
            acc[id] = []
          }

          acc[id].push(item)
        })
      }

      if (collection?.id) {
        if (!acc[collection.id]) {
          acc[collection.id] = []
        }

        acc[collection.id].push(item)
      }

      if (collectionVersion?.id) {
        if (!acc[collectionVersion.id]) {
          acc[collectionVersion.id] = []
        }

        acc[collectionVersion.id].push(item)
      }
      return acc
    }, {})
  }
}


// WEBPACK FOOTER //
// ./src/stores/vlp/store/VLPHierarchyStore.ts